import { AnswerType, OptionSource } from '@/enums';
import { Pagination, Profile } from '@/types';

export interface AdminUser extends Profile {
  provider: string;
  isVerified: boolean;
  hasPendingPasswordReset: boolean;
  createdAt: number;
  updatedAt: number;
}

export interface fetchUsersResponse extends Pagination {
  items: AdminUser[];
}

export interface UpdateUserAsAdminRequest {
  name?: string;
  email?: string;
  isAdmin?: boolean;
  isVerified?: boolean;
  provider?: string;
}

export interface CreateUserAsAdminRequest {
  name: string;
  email: string;
  password: string;
  isAdmin?: boolean;
  provider?: string;
}

// Question related types
export interface AdminQuestion {
  id: string;
  level: number;
  order: number;
  question: string;
  module: { id: string; name: string };
  answerType: AnswerType;
  information: string;
  isRequired: boolean;
  industry?: { id: string; name: string };
  parentQuestionId?: string;
  answerOptionSet?: { id: string; name: string };
  createdAt: number;
  updatedAt: number;
}

export interface CreateQuestionAsAdminRequest {
  question: string;
  module: string;
  information?: string;
  answerType: AnswerType;
  industryId?: string;
  parentQuestionId?: string;
  answerOptionSetId?: string;
}

export interface UpdateQuestionAsAdminRequest {
  question: string;
  answerType: AnswerType;
  module: string;
  industryId?: string;
  information?: string;
  answerOptionSetId?: string;
}

// Question Module related types
export interface AdminQuestionModule {
  id: string;
  name: string;
  order: number;
  createdAt: number;
  updatedAt: number;
}

export interface CreateQuestionModuleAsAdminRequest {
  name: string;
}

export interface UpdateQuestionModuleAsAdminRequest {
  name?: string;
}

// Questionnaire related types
export interface CreateQuestionnaireAsAdminRequest {
  userId: string;
  currentQuestionId?: string;
  isCompleted?: boolean;
  industryId?: string;
}

export interface UpdateQuestionnaireAsAdminRequest {
  currentQuestionId?: string;
  isCompleted?: boolean;
  industryId?: string;
}

// AnswerOption related types
export interface AdminAnswerOption {
  id: string;
  value: string;
  label: string;
  order: number;
  optionSet: {
    id: string;
    name: string;
  };
  createdAt: number;
  updatedAt: number;
}

export interface CreateAnswerOptionAsAdminRequest {
  value: string;
  label: string;
  order?: number;
  optionSetId: string;
}

export interface UpdateAnswerOptionAsAdminRequest {
  value?: string;
  label?: string;
  order?: number;
  optionSetId?: string;
}

export interface AdminAnswerOptionSet {
  id: string;
  name: string;
  description?: string;
  optionSource: OptionSource;
  options: {
    id: string;
    value: string;
    label: string;
    order: number;
  }[];
  createdAt: number;
  updatedAt: number;
}

export interface CreateAnswerOptionSetAsAdminRequest {
  name: string;
  description?: string;
  optionSource?: OptionSource;
  options?: {
    value: string;
    label: string;
    order?: number;
  }[];
}

export interface UpdateAnswerOptionSetAsAdminRequest {
  name?: string;
  description?: string;
  optionSource?: OptionSource;
  options?: {
    id?: string; // 用於更新現有選項，沒有 id 表示新選項
    value: string;
    label: string;
    order?: number;
  }[];
}

// API Log related types
export enum LogType {
  // INFO level logs
  API_REQUEST = 'api_request',
  API_RESPONSE = 'api_response',
  USER_ACTION = 'user_action',
  PERFORMANCE = 'performance',

  // WARN level logs
  API_WARN = 'api_warn',
  BUSINESS = 'business',

  // ERROR level logs
  API_ERROR = 'api_error',
  DATABASE = 'database',
  VALIDATION = 'validation',
  AUTHENTICATION = 'authentication',
  AUTHORIZATION = 'authorization',
  NETWORK = 'network',
  SYSTEM = 'system',
  UNKNOWN = 'unknown',

  // DEBUG level logs
  DEBUG = 'debug',
}

// 輕量級 ApiLog 類型（用於列表顯示，僅包含必要字段）
export interface ApiLogListItem {
  id: string;
  logId: string;
  requestId?: string;
  type: LogType;
  message: string;
  errorName?: string;
  userId?: string;
  requestMethod?: string;
  requestPath?: string;
  statusCode?: number;
  requestDuration?: number;
  createdAt: number;
  updatedAt: number;
  isError: boolean;
  isSuccess: boolean;
}

// 精簡 ApiLog 類型（用於詳情顯示，僅包含必要字段）
export interface ApiLog {
  id: string;
  logId: string;
  requestId?: string;
  type: LogType;
  message: string;
  errorName?: string;
  stack?: string;
  userId?: string;
  requestMethod?: string;
  requestPath?: string;
  statusCode?: number;
  requestDuration?: number;
  metadata?: Record<string, string>;
  createdAt: number;
  updatedAt: number;
  removedAt?: number;
  isError: boolean;
  isSuccess: boolean;
}

export interface ApiLogFilters {
  type?: LogType;
  userId?: string;
  requestId?: string;
  logId?: string;
  statusCode?: number;
  isError?: boolean;
  startDate?: string;
  endDate?: string;
  search?: string;
}

export interface ApiLogListResponse {
  items: ApiLogListItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ApiLogStats {
  total: number;
  byType: Record<LogType, number>;
  byHour: Array<{ hour: string; count: number }>;
  byDay: Array<{ day: string; count: number }>;
  errorRate: number;
  avgResponseTime: number;
  topEndpoints: Array<{ endpoint: string; count: number; avgDuration: number }>;
  topErrors: Array<{ message: string; count: number }>;
}

// Result related types
export interface AdminResult {
  id: string;
  questionnaireId: string;
  user: {
    id: string;
    name: string;
    email: string;
  };
  industry: {
    id: string;
    name: string;
  } | null;
  isComplete: boolean; // 是否有完整的分數和AI描述
  calculatedAt: number;
  createdAt: number;
  updatedAt: number;
}

export interface AdminResultsResponse {
  items: AdminResult[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// Statistics related types
export interface IndustryDistribution {
  industryName: string;
  count: number;
  percentage: number;
}

export interface SdgGoalScore {
  goalName: string;
  averageScore: number;
  maxScore: number;
  totalResponses: number;
}

export interface IndustrySdgScore {
  industryName: string;
  sdgScores: {
    goalName: string;
    averageScore: number;
    count: number;
  }[];
}

export interface ScoreDistribution {
  goalName: string;
  distribution: {
    range: string;
    count: number;
    percentage: number;
  }[];
}

export interface CompanySizeStats {
  category: string;
  averageSdgScore: number;
  count: number;
}

export interface CompletionTrend {
  date: string;
  count: number;
}

export interface ResultStatistics {
  overview: {
    totalResults: number;
    totalCompleted: number;
    averageCompletionTime: number;
  };
  industryDistribution: IndustryDistribution[];
  sdgGoalScores: SdgGoalScore[];
  industrySdgComparison: IndustrySdgScore[];
  scoreDistribution: ScoreDistribution[];
  companySizeByEmployees: CompanySizeStats[];
  companySizeByYears: CompanySizeStats[];
  completionTrend: CompletionTrend[];
}
